/**
 * Screen Share Viewer Component
 *
 * Displays remote screen shares with audio indicators
 */

import React, { useEffect, useRef, useState } from 'react';
import { useRemoteParticipants } from '@livekit/components-react';
import { Track, RemoteTrack } from 'livekit-client';
import { useScreenShare } from '../../hooks/useScreenShare';

interface ScreenShareViewerProps {
  className?: string;
  showParticipantName?: boolean;
  autoFullscreen?: boolean;
}

export const ScreenShareViewer: React.FC<ScreenShareViewerProps> = ({
  className = '',
  showParticipantName = true,
  autoFullscreen = false,
}) => {
  const { remoteScreenShares, hasRemoteScreenShares } = useScreenShare();
  const remoteParticipants = useRemoteParticipants();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedShareIndex, setSelectedShareIndex] = useState(0);
  const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);

  // Attach video tracks to video elements
  useEffect(() => {
    const refs = videoRefs.current;
    remoteParticipants.forEach((participant) => {
      const screenPublication = Array.from(participant.videoTrackPublications.values())
        .find((pub) => pub.source === Track.Source.ScreenShare);

      if (screenPublication?.track) {
        const videoElement = refs.get(participant.identity);
        if (videoElement && screenPublication.track instanceof RemoteTrack) {
          screenPublication.track.attach(videoElement);
        }
      }
    });

    // Cleanup function
    return () => {
      remoteParticipants.forEach((participant) => {
        const screenPublication = Array.from(participant.videoTrackPublications.values())
          .find((pub) => pub.source === Track.Source.ScreenShare);

        if (screenPublication?.track) {
          const videoElement = refs.get(participant.identity);
          if (videoElement && screenPublication.track instanceof RemoteTrack) {
            screenPublication.track.detach(videoElement);
          }
        }
      });
    };
  }, [remoteParticipants]);

  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error('[ScreenShare] Fullscreen error:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  };

  // Auto fullscreen on first screen share. Intentionally keyed only on the
  // screen-share presence — re-running when isFullscreen changes would force the
  // user back into fullscreen after they manually exit.
  useEffect(() => {
    if (autoFullscreen && hasRemoteScreenShares && !isFullscreen) {
      toggleFullscreen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when screen share appears; see comment above
  }, [autoFullscreen, hasRemoteScreenShares]);

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (!hasRemoteScreenShares) {
    return null;
  }

  const currentShare = remoteScreenShares[selectedShareIndex];

  return (
    <div
      ref={containerRef}
      className={`screen-share-viewer ${isFullscreen ? 'fullscreen' : ''} ${className}`}
    >
      {/* Main Screen Share Display */}
      <div className="main-screen-container">
        <video
          ref={(el) => {
            if (el && currentShare) {
              videoRefs.current.set(currentShare.participantId, el);
            }
          }}
          autoPlay
          playsInline
          className="screen-video"
        />

        {/* Participant Info Overlay */}
        {showParticipantName && currentShare && (
          <div className="participant-info">
            <div className="participant-badge">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
              </svg>
              <span>{currentShare.participantName}</span>
              {currentShare.hasAudio && (
                <svg className="w-4 h-4 ml-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 3a1 1 0 011 1v4a1 1 0 11-2 0V4a1 1 0 011-1zm4 8a4 4 0 11-8 0v-1a1 1 0 112 0v1a2 2 0 104 0v-1a1 1 0 112 0v1z" />
                </svg>
              )}
            </div>
          </div>
        )}

        {/* Controls Overlay */}
        <div className="controls-overlay">
          <button
            onClick={toggleFullscreen}
            className="control-button"
            title={isFullscreen ? 'Излез от пълен екран' : 'Пълен екран'}
          >
            {isFullscreen ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Multiple Screen Shares - Thumbnail Selector */}
      {remoteScreenShares.length > 1 && (
        <div className="thumbnails-container">
          {remoteScreenShares.map((share, index) => (
            <button
              key={share.participantId}
              onClick={() => setSelectedShareIndex(index)}
              className={`thumbnail ${index === selectedShareIndex ? 'active' : ''}`}
              title={`Покажи екрана на ${share.participantName}`}
            >
              <video
                ref={(el) => {
                  if (el && index !== selectedShareIndex) {
                    videoRefs.current.set(share.participantId, el);
                  }
                }}
                autoPlay
                playsInline
                muted
                className="thumbnail-video"
              />
              <div className="thumbnail-label">
                <span>{share.participantName}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <style>{`
        .screen-share-viewer {
          position: relative;
          width: 100%;
          height: 100%;
          background-color: #000;
          border-radius: 0.5rem;
          overflow: hidden;
        }

        .screen-share-viewer.fullscreen {
          border-radius: 0;
        }

        .main-screen-container {
          position: relative;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .screen-video {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .participant-info {
          position: absolute;
          top: 1rem;
          left: 1rem;
          z-index: 10;
        }

        .participant-badge {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background-color: rgba(0, 0, 0, 0.7);
          border-radius: 0.5rem;
          color: white;
          font-size: 0.875rem;
          backdrop-filter: blur(4px);
        }

        .controls-overlay {
          position: absolute;
          bottom: 1rem;
          right: 1rem;
          display: flex;
          gap: 0.5rem;
          z-index: 10;
        }

        .control-button {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.75rem;
          background-color: rgba(0, 0, 0, 0.7);
          border: none;
          border-radius: 0.5rem;
          color: white;
          cursor: pointer;
          transition: all 0.2s;
          backdrop-filter: blur(4px);
        }

        .control-button:hover {
          background-color: rgba(0, 0, 0, 0.9);
          transform: scale(1.05);
        }

        .thumbnails-container {
          position: absolute;
          bottom: 1rem;
          left: 1rem;
          display: flex;
          gap: 0.5rem;
          z-index: 10;
          max-width: calc(100% - 8rem);
          overflow-x: auto;
        }

        .thumbnail {
          position: relative;
          width: 120px;
          height: 80px;
          border: 2px solid transparent;
          border-radius: 0.5rem;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.2s;
          background-color: rgba(0, 0, 0, 0.5);
        }

        .thumbnail:hover {
          border-color: rgba(255, 255, 255, 0.5);
          transform: scale(1.05);
        }

        .thumbnail.active {
          border-color: #3b82f6;
        }

        .thumbnail-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .thumbnail-label {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 0.25rem 0.5rem;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.9), transparent);
          color: white;
          font-size: 0.75rem;
          text-align: center;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        }

        .thumbnails-container::-webkit-scrollbar {
          height: 4px;
        }

        .thumbnails-container::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 2px;
        }

        .thumbnails-container::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 2px;
        }

        .thumbnails-container::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }
      `}</style>
    </div>
  );
};
